import localforage from "localforage";


export const safeJSON = {
  parse: (str: string) => {
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  },

  stringify: (obj: string | string[] | object) => {
    try {
      return JSON.stringify(obj);
    } catch (e) {
      return "";
    }
  },
};

export const cacheValue = (
  key: string,
  value: any,
  ttl: number = 30 * 24 * 60 * 1000,
) => {
  localforage.setItem(
    key,
    safeJSON.stringify({
      value,
      ttl: Date.now() + (ttl || 30 * 1000),
    }),
  );
};

export const clearCache = (key: string) => {
  localforage.removeItem(key);
};

export const getValueFromCache = async (key: string) => {
  const value: string = (await localforage.getItem(key)) || "";

  if (!value) return;
  const { ttl, value: _value } = safeJSON.parse(value);

  if (Date.now() > ttl) {
    clearCache(key);

    return;
  }

  return _value;
};

interface PageBasedResponse<T> {
  rows: T[];
  page: number;
  pages: number;
  total: number;
  requestId?: number;
}

interface TokenResponse<T> {
  linkSettings: {
    connectedPlatforms: T[];
    eventIncToken: number;
  };
  requestId: number;
}

type PaginatedApiResponse<T> = PageBasedResponse<T> | TokenResponse<T>;

interface ConnectorPaginationOptions {
  limit?: number;
  maxConcurrentRequests?: number;
  maxRetries?: number;
}

/**
 * Pagination helper function that fetches all data from a page-based paginated API
 * Loads all pages in parallel for complete dataset (useful when API doesn't support search)
 * Handles both PageBasedResponse (with rows) and TokenResponse (with linkSettings.connectedPlatforms) formats
 * Note: TokenResponse format returns complete data in single request, no pagination needed
 * @param apiFunction - The API function that accepts page and limit parameters
 * @param options - Pagination options (limit per request, max concurrent requests, retries)
 * @returns Promise with all combined results in PageBasedResponse format with requestId preserved
 */
export async function paginateAllConnectors<T>(
  apiFunction: (page: number, limit: number) => Promise<PaginatedApiResponse<T>>,
  options: ConnectorPaginationOptions = {},
): Promise<PageBasedResponse<T>> {
  const { limit = 100, maxConcurrentRequests = 3, maxRetries = 3 } = options;

  // First request to get data and detect response format
  const firstResponse = await apiFunction(1, limit);
  
  // If it's Token format, it already contains the complete list - no pagination needed
  if ('linkSettings' in firstResponse) {
    const platforms = firstResponse.linkSettings?.connectedPlatforms || [];
    return {
      rows: platforms,
      page: 1,
      pages: 1,
      total: platforms.length,
      requestId: firstResponse.linkSettings?.eventIncToken
    };
  }

  // PageBasedResponse format - proceed with pagination
  const { pages, total } = firstResponse;
  
  // If we got all data in first request, return it with requestId preserved
  if (pages <= 1) {
    return {
      ...firstResponse,
      requestId: firstResponse.requestId
    };
  }

  // Create array of remaining page numbers to fetch
  const remainingPages = Array.from({ length: pages - 1 }, (_, i) => i + 2);

  // Function to fetch a page with retry logic
  const fetchPageWithRetry = async (
    page: number,
  ): Promise<PageBasedResponse<T>> => {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiFunction(page, limit);
        // Ensure we only get PageBasedResponse for pagination
        if ('rows' in response) {
          return response;
        }
        throw new Error('Unexpected response format during pagination');
      } catch (error) {
        lastError = error;
        if (attempt < maxRetries) {
          // Exponential backoff: wait 1s, 2s, 4s...
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)),
          );
        }
      }
    }

    throw lastError;
  };

  // Execute requests in batches to avoid overwhelming the API
  const responses: PageBasedResponse<T>[] = [firstResponse];
  let latestRequestId = firstResponse.requestId;

  for (let i = 0; i < remainingPages.length; i += maxConcurrentRequests) {
    const batch = remainingPages.slice(i, i + maxConcurrentRequests);
    const batchPromises = batch.map((page) => fetchPageWithRetry(page));

    try {
      const batchResults = await Promise.all(batchPromises);
      responses.push(...batchResults);
      
      // Update requestId to the latest one from the last response in this batch
      const lastResponse = batchResults[batchResults.length - 1];
      if (lastResponse.requestId) {
        latestRequestId = lastResponse.requestId;
      }
    } catch (error) {
      console.error(
        `Failed to fetch batch starting at page ${batch[0]}:`,
        error,
      );
      throw error;
    }
  }

  // Combine all results
  const allRows = responses.flatMap((response) => response.rows);

  return {
    rows: allRows,
    page: 1, // Since we're returning all data, we're effectively on "page 1"
    pages: pages,
    total,
    requestId: latestRequestId // Use the latest requestId from the last successful response
  };
}